import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  CompanyCompliancePolicyService,
  CompanyPolicyClientError,
  type CompanyPolicyDraft,
  type CompanyPolicyImpact,
  type CompanyPolicyRequirement,
  type CompanyPolicyVersion,
} from "@/services/company-compliance-policy.service";

function errorMessage(error: unknown): string {
  if (!(error instanceof CompanyPolicyClientError)) return "Company Compliance policy service is temporarily unavailable.";
  if (error.status === 401) return "Session expired.";
  if (error.status === 403) return "Policy-management permission required.";
  if (error.status === 404) return "Organization or policy unavailable.";
  if (error.status === 409 && error.code.includes("draft")) return "Another administrator changed this draft. Refresh before saving again.";
  if (error.status === 409) return "The active policy changed. Review the latest impact before publishing.";
  return "Company Compliance policy service is temporarily unavailable.";
}
function mutableDraft(value: CompanyPolicyDraft): CompanyPolicyDraft {
  return { ...value, requirements: value.requirements.map((item) => ({ ...item })) };
}
const REQUIREMENT_LABELS: Record<CompanyPolicyRequirement["requirementKey"], string> = {
  license: "License", insurance: "Insurance", w9: "W-9",
};

export function CanonicalCompanyCompliancePolicyEditor({ organizationId }: { organizationId: string }) {
  const [active, setActive] = useState<CompanyPolicyVersion | null>(null);
  const [draft, setDraft] = useState<CompanyPolicyDraft | null>(null);
  const [history, setHistory] = useState<CompanyPolicyVersion[]>([]);
  const [impact, setImpact] = useState<CompanyPolicyImpact | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [canManage, setCanManage] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setBusy(true); setError(null); setImpact(null); setConfirmed(false); setCanManage(true);
    try {
      const workspace = await CompanyCompliancePolicyService.getWorkspace(organizationId);
      setActive(workspace.active); setDraft(workspace.draft ? mutableDraft(workspace.draft) : null);
      setHistory(workspace.history);
    } catch (candidate) {
      if (candidate instanceof CompanyPolicyClientError && candidate.status === 403) setCanManage(false);
      setError(errorMessage(candidate));
    }
    finally { setBusy(false); }
  }, [organizationId]);
  useEffect(() => { void load(); }, [load]);

  const updateRequirement = (key: string, patch: Partial<CompanyPolicyRequirement>) => {
    setDraft((current) => current ? { ...current, requirements: current.requirements.map((item) =>
      item.requirementKey === key ? { ...item, ...patch } : item) } : current);
    setImpact(null); setConfirmed(false);
  };
  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await action(); } catch (candidate) { setError(errorMessage(candidate)); }
    finally { setBusy(false); }
  };

  return <div className="rounded-md border p-3 space-y-4" data-testid="canonical-company-policy-editor">
    <div className="flex items-start justify-between gap-3">
      <div><div className="text-sm font-semibold">Canonical Company Compliance</div>
        <p className="text-xs text-muted-foreground">Versioned organization policy used by Company Compliance onboarding and internal review.</p>
        {active ? <p className="text-xs text-muted-foreground">
          {active.requirements.length} active requirements · Published {active.publishedAt ? new Date(active.publishedAt).toLocaleString() : "date unavailable"}
          {active.publishedByLabel ? ` by ${active.publishedByLabel}` : ""}
        </p> : null}</div>
      <Badge variant="outline">Active version {active?.versionNumber ?? "global default"}</Badge>
    </div>
    {error ? <div className="rounded border border-destructive/40 p-2 text-xs text-destructive">{error}</div> : null}
    {!draft && canManage ? <Button size="sm" disabled={busy} onClick={() => void run(async () => {
      setDraft(mutableDraft(await CompanyCompliancePolicyService.createDraft(organizationId)));
    })}>Create draft from active version</Button> : null}
    {draft && canManage ? <>
      <div className="space-y-2">
        {draft.requirements.map((item) => <div key={item.requirementKey} className="rounded border p-3 space-y-2">
          <div className="flex items-center justify-between"><strong className="text-sm">{item.displayLabel ?? REQUIREMENT_LABELS[item.requirementKey]}</strong>
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={item.enabled}
              onCheckedChange={(value) => updateRequirement(item.requirementKey, { enabled: value === true })}/> Enabled</label></div>
          <div className="grid gap-2 md:grid-cols-3">
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={item.required} disabled={!item.enabled}
              onCheckedChange={(value) => updateRequirement(item.requirementKey, { required: value === true })}/> Required</label>
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={item.issueDateRequired} disabled={!item.enabled}
              onCheckedChange={(value) => updateRequirement(item.requirementKey, { issueDateRequired: value === true })}/> Issue date required</label>
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={item.expirationRequired}
              disabled={!item.enabled || item.requirementKey === "w9"}
              onCheckedChange={(value) => updateRequirement(item.requirementKey, { expirationRequired: value === true })}/> Expiration required</label>
          </div>
          <div className="grid gap-2 md:grid-cols-[1fr_100px]"><Input value={item.instructions ?? ""} placeholder="Safe display instructions"
            onChange={(event) => updateRequirement(item.requirementKey, { instructions: event.target.value })}/>
            <Input type="number" min={0} max={2} value={item.sortOrder} aria-label={`${item.requirementKey} display order`}
              onChange={(event) => updateRequirement(item.requirementKey, { sortOrder: Number(event.target.value) })}/></div>
        </div>)}
      </div>
      <Textarea value={draft.versionNote ?? ""} placeholder="Version note required before publishing"
        onChange={(event) => { setDraft({ ...draft, versionNote: event.target.value }); setImpact(null); setConfirmed(false); }}/>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void run(async () => {
          const saved = await CompanyCompliancePolicyService.updateDraft(organizationId, draft);
          setDraft(mutableDraft(saved)); setImpact(null); toast.success("Draft saved. The active policy was not changed.");
        })}>Save draft</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void run(async () => {
          await CompanyCompliancePolicyService.discardDraft(organizationId, draft); setDraft(null); setImpact(null);
        })}>Discard draft</Button>
        <Button size="sm" disabled={busy || !draft.versionNote?.trim()} onClick={() => void run(async () => {
          setImpact(await CompanyCompliancePolicyService.preview(organizationId, draft)); setConfirmed(false);
        })}>Preview changes</Button>
      </div>
      {impact ? <div className="rounded border p-3 space-y-2 text-xs" data-testid="company-policy-impact">
        <strong>Server impact preview</strong>
        <div>Added: {impact.requirementsAdded.join(", ") || "None"}</div>
        <div>Removed: {impact.requirementsRemoved.join(", ") || "None"}</div>
        <div>Rules changed: {impact.rulesChanged.join(", ") || "None"}</div>
        <div>Affected contractors: {impact.affectedCompanyRecordCount}</div>
        <div>Currently operationally verified: {impact.currentlyOperationallyVerifiedRecordCount}</div>
        <div>May become in progress: {impact.recordsThatMayBecomeInProgress}</div>
        <div>Missing a newly required requirement: {impact.recordsMissingNewRequirement}</div>
        <div>Existing evidence that may remain usable: {impact.existingEvidenceMayRemainUsable}</div>
        <div>Existing evidence that cannot satisfy changed rules: {impact.existingEvidenceCannotSatisfyNewRules}</div>
        <label className="flex items-start gap-2"><Checkbox checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)}/>
          I understand this policy version may change compliance readiness for existing contractors.</label>
        <Button size="sm" variant="destructive" disabled={busy || !confirmed} onClick={() => void run(async () => {
          await CompanyCompliancePolicyService.publish(organizationId, draft, impact);
          toast.success("Canonical Company Compliance policy published."); await load();
        })}>Publish new version</Button>
      </div> : null}
    </> : null}
    <div className="space-y-1"><div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Immutable policy history</div>
      {history.length ? history.map((item) => <div key={item.id} className="rounded border px-2 py-1 text-xs">
        Version {item.versionNumber} · {item.status} · {item.publishedAt ? new Date(item.publishedAt).toLocaleString() : "Not published"}
        {item.publishedByLabel ? ` · ${item.publishedByLabel}` : ""}
        {item.versionNote ? ` · ${item.versionNote}` : ""}
      </div>) : <p className="text-xs text-muted-foreground">No organization-specific published versions yet.</p>}</div>
  </div>;
}
